import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, extractError } from "@/lib/api";
import { PageContainer, PageHeader, EmptyState } from "@/components/app/PageShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import {
    FilesIcon, Plus, PencilSimple, Database, Trash, ShareNetwork, Eye, UploadSimple, CheckCircle, Warning, Sparkle,
} from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";
import { ShareFormDialog } from "@/components/app/ShareFormDialog";
import { TemplatesGalleryDialog } from "@/components/app/TemplatesGalleryDialog";
import { toast } from "sonner";

export default function FormsListPage() {
    const [forms, setForms] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ form_name: "", form_title: "", form_code: "", description: "" });
    const [shareForm, setShareForm] = useState(null);

    // Import state
    const fileInputRef = useRef(null);
    const [importBundle, setImportBundle] = useState(null);   // parsed JSON
    const [importDraft, setImportDraft] = useState({ new_form_name: "", new_form_code: "" });
    const [importBusy, setImportBusy] = useState(false);
    const [importResult, setImportResult] = useState(null);   // skipped summary

    const [galleryOpen, setGalleryOpen] = useState(false);

    const navigate = useNavigate();
    const { user } = useAuth();
    const isAdmin = user?.role === "platform_admin" || user?.role === "org_admin";

    const load = async () => {
        try { const { data } = await api.get("/forms"); setForms(data); }
        catch (e) { toast.error(extractError(e)); }
    };
    useEffect(() => { load(); }, []);

    const create = async (e) => {
        e.preventDefault();
        try {
            const code = (form.form_code || form.form_name).toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
            const { data } = await api.post("/forms", {
                form_name: form.form_name,
                form_title: form.form_title || form.form_name,
                description: form.description,
                form_code: code,
            });
            toast.success("Form created");
            setOpen(false); setForm({ form_name: "", form_title: "", form_code: "", description: "" });
            navigate(`/forms/${data.id}/builder`);
        } catch (err) { toast.error(extractError(err)); }
    };

    const del = async (id) => {
        if (!confirm("Delete this form and all its records?")) return;
        try { await api.delete(`/forms/${id}`); toast.success("Deleted"); load(); }
        catch (e) { toast.error(extractError(e)); }
    };

    const onPickFile = (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";  // reset for re-picks
        if (!file) return;
        const reader = new FileReader();
        reader.onerror = () => toast.error("Could not read file");
        reader.onload = () => {
            try {
                const json = JSON.parse(reader.result);
                if (!json?.form?.form_code) throw new Error("Not a FormFlo bundle (missing form.form_code)");
                setImportBundle(json);
                // Suggest unique code if it already exists in current org
                const existing = forms.find((f) => f.form_code === json.form.form_code);
                setImportDraft({
                    new_form_name: json.form.form_name || "",
                    new_form_code: existing ? `${json.form.form_code}_copy` : json.form.form_code,
                });
                setImportResult(null);
            } catch (err) {
                toast.error(`Invalid bundle: ${err.message}`);
            }
        };
        reader.readAsText(file);
    };

    const doImport = async () => {
        setImportBusy(true);
        try {
            const { data } = await api.post("/forms/import", {
                bundle: importBundle,
                new_form_code: importDraft.new_form_code || undefined,
                new_form_name: importDraft.new_form_name || undefined,
            });
            toast.success(`Imported as "${data.form_name}"`);
            setImportResult(data);
            await load();
        } catch (e) { toast.error(extractError(e)); }
        finally { setImportBusy(false); }
    };

    const closeImport = () => { setImportBundle(null); setImportDraft({ new_form_name: "", new_form_code: "" }); setImportResult(null); };

    return (
        <PageContainer>
            <PageHeader
                overline="Build"
                title="Forms"
                description="Metadata-driven forms. Define once, render anywhere."
                actions={
                    isAdmin && (
                        <div className="flex items-center gap-2">
                            <Button
                                variant="outline"
                                className="rounded-sm"
                                onClick={() => setGalleryOpen(true)}
                                data-testid="open-templates-gallery-btn"
                            >
                                <Sparkle size={16} className="mr-2" /> Templates
                            </Button>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept=".json,application/json"
                                onChange={onPickFile}
                                className="hidden"
                                data-testid="import-file-input"
                            />
                            <Button
                                variant="outline"
                                className="rounded-sm"
                                onClick={() => fileInputRef.current?.click()}
                                data-testid="import-form-btn"
                            >
                                <UploadSimple size={16} className="mr-2" /> Import
                            </Button>
                            <Dialog open={open} onOpenChange={setOpen}>
                                <DialogTrigger asChild>
                                    <Button className="rounded-sm bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="new-form-btn"><Plus size={16} className="mr-2" /> New form</Button>
                                </DialogTrigger>
                                <DialogContent className="rounded-sm" data-testid="form-dialog">
                                    <DialogHeader><DialogTitle>New form</DialogTitle></DialogHeader>
                                    <form onSubmit={create} className="space-y-4">
                                        <div><Label className="label-overline">Form name (internal)</Label><Input required value={form.form_name} onChange={(e) => setForm({ ...form, form_name: e.target.value })} placeholder="inventory_request" className="mt-2 rounded-sm" data-testid="form-name-input" /></div>
                                        <div><Label className="label-overline">Form title (header)</Label><Input value={form.form_title} onChange={(e) => setForm({ ...form, form_title: e.target.value })} placeholder="Defaults to name" className="mt-2 rounded-sm" data-testid="form-title-input" /></div>
                                        <div>
                                            <Label className="label-overline">Form code</Label>
                                            <Input value={form.form_code} onChange={(e) => setForm({ ...form, form_code: e.target.value })} placeholder="auto from name" className="mt-2 rounded-sm font-mono" data-testid="form-code-input" />
                                            <p className="text-xs text-muted-foreground mt-1">Unique identifier. Used in permission strings.</p>
                                        </div>
                                        <div><Label className="label-overline">Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-2 rounded-sm" data-testid="form-desc-input" /></div>
                                        <DialogFooter><Button type="submit" className="rounded-sm bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="save-form-btn">Create & open builder</Button></DialogFooter>
                                    </form>
                                </DialogContent>
                            </Dialog>
                        </div>
                    )
                }
                testid="forms-header"
            />

            {forms.length === 0 ? (
                <EmptyState icon={FilesIcon} title="No forms yet" description={isAdmin ? "Create your first form, or import one." : "Ask an admin to create a form."} testid="forms-empty" />
            ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {forms.map((f) => (
                        <div key={f.id} className="brut-card p-6" data-testid={`form-card-${f.id}`}>
                            <div className="flex items-start justify-between mb-3">
                                <Badge variant="outline" className="rounded-sm font-mono text-[10px]">{f.status}</Badge>
                                <span className="text-xs text-muted-foreground font-mono">v{f.version}</span>
                            </div>
                            <h3 className="text-lg font-bold tracking-tight mb-1">{f.form_title || f.form_name}</h3>
                            <div className="text-xs font-mono text-muted-foreground mb-3">{f.form_code}</div>
                            {f.description && <p className="text-sm text-muted-foreground mb-4 line-clamp-2">{f.description}</p>}
                            <div className="flex gap-2 mt-4 pt-4 border-t border-border">
                                <Link to={`/forms/${f.id}/fill`} className="flex-1">
                                    <Button variant="outline" size="sm" className="rounded-sm w-full" data-testid={`fill-btn-${f.id}`}><Eye size={14} className="mr-1" /> Fill</Button>
                                </Link>
                                <Link to={`/forms/${f.id}/records`}>
                                    <Button variant="outline" size="sm" className="rounded-sm" data-testid={`records-btn-${f.id}`}><Database size={14} /></Button>
                                </Link>
                                {isAdmin && (
                                    <>
                                        <Button variant="outline" size="sm" className="rounded-sm" onClick={() => setShareForm(f)} data-testid={`share-form-${f.id}`}><ShareNetwork size={14} /></Button>
                                        <Link to={`/forms/${f.id}/builder`}>
                                            <Button variant="outline" size="sm" className="rounded-sm" data-testid={`edit-form-${f.id}`}><PencilSimple size={14} /></Button>
                                        </Link>
                                        <Button variant="outline" size="sm" className="rounded-sm" onClick={() => del(f.id)} data-testid={`del-form-${f.id}`}><Trash size={14} /></Button>
                                    </>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <ShareFormDialog
                form={shareForm}
                open={!!shareForm}
                onOpenChange={(v) => !v && setShareForm(null)}
                onSaved={load}
            />

            <TemplatesGalleryDialog
                open={galleryOpen}
                onOpenChange={setGalleryOpen}
                onUsed={(d) => { load(); navigate(`/forms/${d.form_id}/builder`); }}
            />

            {/* Import dialog */}
            <Dialog open={!!importBundle} onOpenChange={(v) => !v && closeImport()}>
                <DialogContent className="rounded-sm max-w-2xl" data-testid="import-dialog">
                    {importBundle && !importResult && (
                        <>
                            <DialogHeader>
                                <DialogTitle>Import form</DialogTitle>
                                <DialogDescription>
                                    Review the bundle and confirm. You can rename the form before creating it.
                                </DialogDescription>
                            </DialogHeader>
                            <div className="space-y-4">
                                <div className="border border-border rounded-sm p-3 bg-secondary/40 text-sm" data-testid="import-bundle-meta">
                                    <div className="grid grid-cols-2 gap-2 text-xs">
                                        <div><span className="label-overline">Source:</span> {importBundle.source_org?.name} <span className="text-muted-foreground">({importBundle.source_org?.slug})</span></div>
                                        <div><span className="label-overline">Exported:</span> {new Date(importBundle.exported_at).toLocaleDateString()}</div>
                                        <div><span className="label-overline">Sections:</span> {(importBundle.sections || []).length}</div>
                                        <div><span className="label-overline">Fields:</span> {(importBundle.fields || []).length}</div>
                                        <div><span className="label-overline">Workflow steps:</span> {(importBundle.form?.workflow_steps || []).length}</div>
                                        <div><span className="label-overline">Access configs:</span> {(importBundle.form_access_configs || []).length}</div>
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <Label className="label-overline">New form name</Label>
                                        <Input value={importDraft.new_form_name} onChange={(e) => setImportDraft({ ...importDraft, new_form_name: e.target.value })} className="mt-2 rounded-sm" data-testid="import-new-name-input" />
                                    </div>
                                    <div>
                                        <Label className="label-overline">New form code</Label>
                                        <Input value={importDraft.new_form_code} onChange={(e) => setImportDraft({ ...importDraft, new_form_code: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "") })} className="mt-2 rounded-sm font-mono" data-testid="import-new-code-input" />
                                    </div>
                                </div>
                                <div className="text-xs text-muted-foreground">
                                    Groups, roles and users are matched by name/code in your org. Any unmatched references will be skipped — review the summary after import.
                                </div>
                            </div>
                            <DialogFooter>
                                <Button variant="ghost" onClick={closeImport} className="rounded-sm">Cancel</Button>
                                <Button onClick={doImport} disabled={importBusy} className="rounded-sm bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="confirm-import-btn">
                                    {importBusy ? "Importing…" : "Import form"}
                                </Button>
                            </DialogFooter>
                        </>
                    )}
                    {importResult && (
                        <>
                            <DialogHeader>
                                <DialogTitle className="flex items-center gap-2"><CheckCircle size={20} weight="fill" className="text-success" /> Imported</DialogTitle>
                                <DialogDescription>Form created successfully. Review skipped references below if any.</DialogDescription>
                            </DialogHeader>
                            <div className="space-y-3 text-sm" data-testid="import-result">
                                <div className="grid grid-cols-3 gap-3">
                                    <div className="brut-section p-3"><div className="label-overline">Sections</div><div className="text-2xl font-bold mt-1">{importResult.sections_count}</div></div>
                                    <div className="brut-section p-3"><div className="label-overline">Fields</div><div className="text-2xl font-bold mt-1">{importResult.fields_count}</div></div>
                                    <div className="brut-section p-3"><div className="label-overline">Workflow steps</div><div className="text-2xl font-bold mt-1">{importResult.workflow_steps_count}</div></div>
                                </div>
                                {(importResult.skipped?.groups?.length || importResult.skipped?.roles?.length || importResult.skipped?.users?.length || importResult.skipped?.workflow_steps?.length || importResult.skipped?.configs?.length) ? (
                                    <div className="border border-warning/40 bg-warning/10 rounded-sm p-3">
                                        <div className="flex items-center gap-2 text-xs font-semibold mb-2"><Warning size={14} /> Skipped references</div>
                                        <ul className="text-xs space-y-1">
                                            {importResult.skipped.groups?.length > 0 && <li>Groups: <code className="font-mono">{importResult.skipped.groups.join(", ")}</code></li>}
                                            {importResult.skipped.roles?.length > 0 && <li>Roles: <code className="font-mono">{importResult.skipped.roles.join(", ")}</code></li>}
                                            {importResult.skipped.users?.length > 0 && <li>Users: <code className="font-mono">{importResult.skipped.users.join(", ")}</code></li>}
                                            {importResult.skipped.workflow_steps?.length > 0 && <li>Workflow steps: {importResult.skipped.workflow_steps.length}</li>}
                                            {importResult.skipped.configs?.length > 0 && <li>Access configs: {importResult.skipped.configs.length}</li>}
                                        </ul>
                                    </div>
                                ) : null}
                            </div>
                            <DialogFooter>
                                <Button variant="outline" onClick={closeImport} className="rounded-sm">Close</Button>
                                <Button onClick={() => { const fid = importResult.form_id; closeImport(); navigate(`/forms/${fid}/builder`); }} className="rounded-sm bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="open-imported-btn">
                                    Open in builder
                                </Button>
                            </DialogFooter>
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </PageContainer>
    );
}
